/**
 * Conversation Headers Facade
 * 
 * Platform-agnostic facade for conversation header processing (100% shared code).
 * Handles clarification, reflection, repeated conversation flags, and readiness states.
 * 
 * Part of the Conversation Page migration (Phase 1.3).
 */

import { useState, useCallback } from 'react';
import { ConversationHeaders } from '../../../types/conversation.types';
import { getBestConversationId } from '../../../utils';
import { mapNodeTypeToQuestionnaireType } from '../../../utils/questionnaireUtils';

// ==================== Types ====================

export interface ConversationHeadersState {
  isClarification: boolean;
  isReflection: boolean;
  isRepeated: boolean;
  isPrototyper: boolean;
  isReadyToCreate: boolean;
  isReadyToCreateAccount: boolean;
  readyToGenerate: string | null;
}

export interface ConversationHeadersFacade {
  // State
  isClarification: boolean;
  isReflection: boolean;
  isRepeated: boolean;
  isPrototyper: boolean;
  isReadyToCreate: boolean;
  isReadyToCreateAccount: boolean;
  readyToGenerate: string | null;
  
  // Actions
  processHeaders: (
    headers: ConversationHeaders | undefined,
    currentConversationId: string | null,
    newConversationId?: string,
    actualConversationId?: string | null,
    urlConversationId?: string | null,
    conversationId?: string
  ) => void;
  processHeadersFromLastMessage: (messages: any[], conversationId?: string) => void;
  setIsClarification: (value: boolean) => void;
  setIsReflection: (value: boolean) => void;
  setIsRepeated: (value: boolean) => void;
  setIsPrototyper: (value: boolean) => void;
  setIsReadyToCreate: (value: boolean) => void;
  setIsReadyToCreateAccount: (value: boolean) => void;
  setReadyToGenerate: (value: string | null) => void;
  resetHeaders: () => void;
}

// ==================== Facade Hook ====================

export interface UseConversationHeadersFacadeParams {
  // Dependencies injected from platform (web/plugin)
  onProjectCreationReady?: (conversationId: string) => void;
  onDrawingHeadersProcessed?: (headers: ConversationHeaders, currentId: string | null, newId?: string) => void;
  onAccountCreationRequired?: () => void;
  isLoggedIn: boolean;
}

export const useConversationHeadersFacade = ({
  onProjectCreationReady,
  onDrawingHeadersProcessed,
  onAccountCreationRequired,
  isLoggedIn
}: UseConversationHeadersFacadeParams): ConversationHeadersFacade => {
  // ==================== State ====================
  
  const [isClarification, setIsClarification] = useState(false);
  const [isReflection, setIsReflection] = useState(false);
  const [isRepeated, setIsRepeated] = useState(false);
  const [isPrototyper, setIsPrototyper] = useState(false);
  const [isReadyToCreate, setIsReadyToCreate] = useState(false);
  const [isReadyToCreateAccount, setIsReadyToCreateAccount] = useState(false);
  const [readyToGenerate, setReadyToGenerate] = useState<string | null>(null);

  // ==================== Actions ====================

  /**
   * Reset all header states
   */
  const resetHeaders = useCallback(() => {
    setIsClarification(false);
    setIsReflection(false);
    setIsRepeated(false);
    setIsPrototyper(false);
    setIsReadyToCreate(false);
    setIsReadyToCreateAccount(false);
    setReadyToGenerate(null);
  }, []);

  /**
   * Process headers from last assistant message in conversation history
   * This is called after loading conversation history to restore header state
   */
  const processHeadersFromLastMessage = useCallback((messages: any[], conversationId?: string) => {
    // Reset headers first
    resetHeaders();

    // Find last assistant message
    const lastAssistantMessage = [...messages]
      .reverse()
      .find(msg => msg.role === 'assistant');

    if (!lastAssistantMessage) return;

    // Process actionType
    if (lastAssistantMessage.actionType) {
      switch (lastAssistantMessage.actionType) {
        case 'clarification':
          setIsClarification(true);
          break;
        case 'reflection':
          setIsReflection(true);
          break;
        case 'repetition':
          setIsRepeated(true);
          break;
      }
    }

    // Process nodeType for drawing
    if (lastAssistantMessage.nodeType) {
      const questionnaireType = mapNodeTypeToQuestionnaireType(lastAssistantMessage.nodeType);
      if (questionnaireType) {
        // Notify drawing facade through callback with conversation ID
        onDrawingHeadersProcessed?.(
          { canBeDrawn: questionnaireType } as ConversationHeaders,
          conversationId || null, // Pass conversation ID if available
          undefined
        );
      }
    }

    // Process readyToGenerate
    if (lastAssistantMessage.readyToGenerate) {
      setReadyToGenerate(lastAssistantMessage.readyToGenerate);
    }

    // Process mode (prototyper)
    if (lastAssistantMessage.mode === 'prototyper') {
      setIsPrototyper(true);
    }
  }, [resetHeaders, onDrawingHeadersProcessed]);

  /**
   * Process conversation headers and update state
   * 
   * This handles:
   * - Clarification flag (AI asking for clarification)
   * - Reflection flag (AI suggesting reflection)
   * - Repeated flag (duplicate/similar conversation)
   * - Ready to create project (from conversation)
   * - Ready to create account (user needs to sign up)
   * - Ready to generate drawing
   */
  const processHeaders = useCallback((
    headers: ConversationHeaders | undefined,
    currentConversationId: string | null,
    newConversationId?: string,
    actualConversationId?: string | null,
    urlConversationId?: string | null,
    conversationId?: string
  ) => {
    if (!headers) return;

    // Update header flags
    setIsClarification(!!headers.clarification);
    setIsReflection(!!headers.reflection);
    setIsRepeated(!!headers.repeated);
    setIsPrototyper(!!headers.prototyper);
    setReadyToGenerate(headers.readyToGenerate || null);

    // Check if ready to create account
    const isAccountCreationReady = !!headers.isReadyToCreateAccount;
    setIsReadyToCreateAccount(isAccountCreationReady);

    // Handle account creation requirement (user not logged in but needs account)
    if (isAccountCreationReady && !isLoggedIn) {
      onAccountCreationRequired?.();
    }

    // Check if ready to create project
    const isProjectCreationReady = !!headers.isReadyToCreate;
    
    // Handle project creation readiness
    if (isProjectCreationReady) {
      setIsReadyToCreate(true);
      
      // Determine the best conversation ID to use
      const idToUse = getBestConversationId(
        actualConversationId,
        urlConversationId,
        newConversationId,
        conversationId
      );

      if (isLoggedIn && idToUse) {
        // Notify platform that project creation is ready
        onProjectCreationReady?.(idToUse);
      } else if (!isLoggedIn) {
        // User needs to log in first
        onAccountCreationRequired?.();
      }
    } else {
      setIsReadyToCreate(false);
    }

    // Notify platform about drawing headers
    onDrawingHeadersProcessed?.(headers, currentConversationId, newConversationId);
  }, [
    isLoggedIn,
    onProjectCreationReady,
    onDrawingHeadersProcessed,
    onAccountCreationRequired
  ]);

  // ==================== Return Facade ====================

  return {
    // State
    isClarification,
    isReflection,
    isRepeated,
    isPrototyper,
    isReadyToCreate,
    isReadyToCreateAccount,
    readyToGenerate,
    
    // Actions
    processHeaders,
    processHeadersFromLastMessage,
    setIsClarification,
    setIsReflection,
    setIsRepeated,
    setIsPrototyper,
    setIsReadyToCreate,
    setIsReadyToCreateAccount,
    setReadyToGenerate,
    resetHeaders
  };
};
